import { useLayoutEffect, useRef } from "react";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { stagger, useAnimate, useReducedMotion } from "framer-motion";
import ctaFamily from "../../assets/landing/cta-family.webp";
import { useSponsors } from "../../lib/api/useSponsors";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { Sparkle } from "../decor";
import { Scene } from "./stage/Scene";
import { SplitWords } from "./stage/Split";
import { useStage } from "./stage/context";
import * as fx from "./stage/fx";
import type { DomStep, Timeline } from "./stage/choreography";

const SCENE_ID = "start";

/**
 * Barn doors: the card opens from its vertical centre line outward. The last
 * frame lets the clip go past the box so the card's glow shadow survives.
 */
const BARN_DOORS: DomStep = {
  kind: "dom",
  target: "[data-fx=card]",
  keyframes: {
    clipPath: [
      "inset(0% 50% 0% 50% round 2.5rem)",
      "inset(0% 0% 0% 0% round 2.5rem)",
      "inset(-12% -6% -24% -6% round 2.5rem)",
    ],
  },
  options: { duration: 0.65, times: [0, 0.95, 1], ease: [fx.EASE.inOut, "linear"] },
};

/** The family climbs up from behind the card's bottom edge (the card clips them). */
const FAMILY_RISE: DomStep = {
  kind: "dom",
  target: "[data-fx=family]",
  keyframes: { y: ["100%", "0%"] },
  options: { duration: 0.75, ease: fx.EASE.out, at: "-0.3" },
};

/** Confetti: each sparkle bursts past full size with a twist and settles, left → right. */
const SPARKLE_BURST: DomStep = {
  kind: "dom",
  target: "[data-fx=spark]",
  keyframes: { opacity: [0, 1, 1], scale: [0, 1.3, 1], rotate: [-40, 12, 0] },
  options: {
    duration: 0.5,
    times: [0, 0.55, 1],
    ease: ["easeOut", "easeInOut"],
    delay: stagger(0.04),
    at: "-0.5",
  },
};

/*
 * Signature: barn doors + confetti. The card swings open from the middle, the
 * headline pops in word by word, then the sub, the buttons, the family rising
 * into view and a left-to-right burst of sparkles. Sponsor logos come last.
 */
const TIMELINE: Timeline = [
  BARN_DOORS,
  fx.pop("[data-fx=title] [data-word]", { stagger: 0.08, at: "-0.3" }),
  fx.fade("[data-fx=sub]", { at: "-0.4" }),
  fx.pop("[data-fx=cta]", { from: 0.7, at: "-0.25" }),
  FAMILY_RISE,
  SPARKLE_BURST,
  fx.fade("[data-fx=sponsor]", { stagger: 0.06, at: "-0.3" }),
];

/**
 * Confetti positions inside the card, in % of its box: twinkles and round
 * dots. Ordered by `left` so the DOM-order stagger runs left → right; kept
 * clear of the copy (centre top) and of the family (centre bottom). Only the
 * `phone` ones show on the small card, where the copy fills the width.
 */
const SPARKS = [
  { left: "4%", top: "17%", size: "h-6 w-6 sm:h-8 sm:w-8", tone: "text-white", dot: false, phone: true },
  { left: "7%", top: "63%", size: "h-2.5 w-2.5", tone: "bg-amber-200", dot: true, phone: false },
  { left: "12%", top: "39%", size: "h-4 w-4 sm:h-5 sm:w-5", tone: "text-sky-100", dot: false, phone: false },
  { left: "17%", top: "79%", size: "h-5 w-5 sm:h-6 sm:w-6", tone: "text-pink-200", dot: false, phone: false },
  { left: "23%", top: "55%", size: "h-2 w-2", tone: "bg-white/90", dot: true, phone: false },
  { left: "76%", top: "57%", size: "h-2 w-2", tone: "bg-pink-200", dot: true, phone: false },
  { left: "81%", top: "81%", size: "h-5 w-5 sm:h-6 sm:w-6", tone: "text-amber-200", dot: false, phone: false },
  { left: "86%", top: "39%", size: "h-4 w-4 sm:h-5 sm:w-5", tone: "text-white", dot: false, phone: false },
  { left: "91%", top: "65%", size: "h-2.5 w-2.5", tone: "bg-sky-100", dot: true, phone: false },
  { left: "93%", top: "15%", size: "h-6 w-6 sm:h-8 sm:w-8", tone: "text-amber-200", dot: false, phone: true },
] as const;

const ctaBase = cn(
  "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[color,background-color,box-shadow] sm:px-6 sm:py-3 sm:text-base",
  FOCUS_RING,
  // A sky ring vanishes on the gradient; white on an indigo offset doesn't.
  "focus-visible:ring-white focus-visible:ring-offset-indigo-600",
);

export function GetStarted() {
  return (
    <Scene id={SCENE_ID} label="Get started" timeline={TIMELINE} className="bg-gradient-to-b from-white via-indigo-50/40 to-white">
      {/* Backdrop: soft colour fields and a whisper of grain. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-10 h-[28rem] w-[28rem] rounded-full bg-sky-200/40 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-[30rem] w-[30rem] rounded-full bg-violet-200/40 blur-3xl" />
        <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-rose-100/50 blur-3xl" />
        <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 pb-10 pt-20">
        <div data-fx="card" className="mx-auto w-full max-w-5xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-500 shadow-glow">
            {/* Shade: deepens the upper card so white and sky-100 copy clear AA
                contrast on every part of the gradient, not just the violet end. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-indigo-950/40 via-indigo-950/25 to-transparent"
            />

            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
              {SPARKS.map((s, i) => (
                <span
                  key={i}
                  data-fx="spark"
                  className={cn("absolute", s.size, !s.phone && "hidden sm:block")}
                  style={{ left: s.left, top: s.top }}
                >
                  <span className="block h-full w-full animate-bob" style={{ animationDelay: `${(i % 5) * -0.8}s` }}>
                    {s.dot ? (
                      <span className={cn("block h-full w-full rounded-full", s.tone)} />
                    ) : (
                      <Sparkle className={cn("h-full w-full", s.tone)} />
                    )}
                  </span>
                </span>
              ))}
            </div>

            <div className="relative px-6 pt-9 text-center sm:px-12 sm:pt-12 lg:pt-14">
              <h2
                data-fx="title"
                className="mx-auto max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl"
              >
                <SplitWords text="Start your family's list today." />
              </h2>
              <p data-fx="sub" className="mx-auto mt-3 max-w-xl text-balance text-[15px] text-sky-100 sm:mt-4 sm:text-lg">
                Set it up in minutes. Share it in seconds. Sleep easier tonight.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3 sm:mt-7">
                <Link
                  href="/register"
                  data-fx="cta"
                  className={cn(ctaBase, "bg-white text-sky-700 shadow-soft hover:bg-sky-50 hover:shadow-glow")}
                >
                  Get started — it's free <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  href="/login"
                  data-fx="cta"
                  className={cn(ctaBase, "border border-white/60 text-white hover:bg-white/10")}
                >
                  Sign in
                </Link>
              </div>
            </div>

            {/* Width follows the screen's height too, so the card fits short
                laptops. The negative margin tucks their soles behind the card's
                edge: they stand in it, which is what they rise out of. */}
            <div data-fx="family" className="relative mx-auto mt-5 w-[min(100%,32rem,50vh)] sm:mt-6">
              <img
                src={ctaFamily}
                alt="The whole family waving hello"
                width={1040}
                height={691}
                loading="lazy"
                draggable={false}
                className="-mb-[3%] block h-auto w-full select-none"
              />
            </div>
          </div>
        </div>

        <SponsorBand />
      </div>
    </Scene>
  );
}

/**
 * Landing-page sponsor band, under the get-started card.
 *
 * Renders NOTHING until there is at least one active sponsor — an empty
 * "Supported by" heading over blank space reads as a broken page, and this
 * section is genuinely optional. It also stays silent while loading and on
 * error: a sponsor strip is never worth a spinner or an error message on a
 * marketing page.
 */
function SponsorBand() {
  const { data: sponsors } = useSponsors();
  const { arrivedId } = useStage();
  const reduce = useReducedMotion();
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const count = sponsors?.length ?? 0;

  const arrived = useRef(false);
  useLayoutEffect(() => {
    arrived.current = arrivedId === SCENE_ID;
  });

  // The scene poses its entrance targets when it mounts, but the logos only
  // arrive from the network afterwards. Pose them here so they don't sit in
  // plain view while the page glides onto the scene; if the scene is already
  // showing, bring them straight in instead.
  useLayoutEffect(() => {
    const el = scope.current;
    if (!el || reduce || count === 0) return;
    const items = el.querySelectorAll<HTMLElement>("[data-fx=sponsor]");
    if (arrived.current) {
      animate(items, { opacity: [0, 1], y: [10, 0] }, { duration: 0.45, delay: stagger(0.06) });
    } else {
      animate(items, { opacity: 0, y: 10 }, { duration: 0 });
    }
  }, [count, reduce, scope, animate]);

  if (!sponsors || sponsors.length === 0) return null;

  return (
    <div ref={scope} className="mt-6 sm:mt-8">
      <p
        id="sponsors-label"
        data-fx="sponsor"
        className="text-center text-xs font-semibold uppercase tracking-wide text-sky-700 sm:text-sm"
      >
        Supported by
      </p>
      <ul
        aria-labelledby="sponsors-label"
        className="mt-3 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 sm:mt-4 sm:gap-x-12 sm:gap-y-4"
      >
        {sponsors.map((s) => {
          const logo = s.logoUrl ? (
            <img
              src={s.logoUrl}
              alt={s.name}
              loading="lazy"
              /* Uniform height, natural width: logos arrive at every aspect
                 ratio, and a fixed box would letterbox or crop them. Kept
                 shorter than a standalone band would use (h-9/h-11) so the
                 band still fits under the card on one screen; most wordmarks
                 carry internal padding, so don't go smaller. The width cap
                 (10rem on phones, 14rem above) stops wide wordmarks all
                 clamping to the SAME width, which would make a varied set look
                 uniform again. */
              className="h-9 w-auto max-w-[10rem] object-contain opacity-80 transition-opacity duration-200 hover:opacity-100 sm:h-11 sm:max-w-[14rem]"
            />
          ) : (
            <span className="font-display text-lg font-semibold text-neutral-700">{s.name}</span>
          );

          return (
            <li key={s.id} data-fx="sponsor">
              {s.websiteUrl ? (
                <a
                  href={s.websiteUrl}
                  target="_blank"
                  /* noopener/noreferrer: these are third-party links an
                     operator typed in, so never hand them window.opener. */
                  rel="noopener noreferrer sponsored"
                  className="inline-flex rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                  aria-label={`${s.name} — opens in a new tab`}
                >
                  {logo}
                </a>
              ) : (
                logo
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
